import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Camera, Check, Eye, Menu, Sparkles, Upload, X } from "lucide-react";
import { detectFaceForReading, loadFaceReadModels, snapshotVideo } from "@/lib/faceapi";
import { requestFaceAnalysis, requestHairColor, requestHairEdit, requestHairFront, requestHairSheet } from "@/lib/hairEdit";
import {
  FILTERS,
  buildReading,
  colorReason,
  hairSrc,
  measureLandmarks,
  readSkinTone,
  recommendColors,
  recommendStyles,
  hasRef,
  refImage,
  styleReason,
} from "@/lib/faceReading";
import { recommendStyleFamilies, setStyleFamilies, styleVariantImage, variantGridClass } from "@/lib/styleFamilies";
import { fetchCatalog, fetchPublicConfig, forgotPassword, unlockVisitor } from "@/lib/siteApi";
import PasswordField from "@/components/PasswordField";

const serif = '"Cormorant Garamond", Georgia, serif';

function useFonts() {
  useEffect(() => {
    const id = "hair-consult-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;0,700;1,500&family=Manrope:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);
}

function Btn({ children, onClick, variant = "solid", className = "", type = "button", disabled = false, testid }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-full px-5 min-h-11 text-sm font-medium transition disabled:opacity-50";
  const look = variant === "solid"
    ? "bg-[#1c1917] text-white hover:bg-[#2c2622]"
    : variant === "light"
      ? "bg-white text-[#1c1917] hover:bg-[#f3ece4]"
      : "border border-[#d9d0c6] bg-white text-[#1c1917] hover:bg-[#f7f3ee]";
  return (
    <button type={type} onClick={onClick} disabled={disabled} data-testid={testid} className={`${base} ${look} ${className}`}>
      {children}
    </button>
  );
}

function Stepper({ step }) {
  const steps = ["Upload Foto", "Analisa AI", "Hasil & Rekomendasi"];
  return (
    <ol className="flex items-start justify-center" data-testid="stepper">
      {steps.map((label, i) => {
        const n = i + 1;
        const on = step >= n;
        return (
          <li key={label} className="flex items-start">
            <div className="flex w-[78px] flex-col items-center sm:w-[130px]">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs ${on ? "border-[#1c1917] bg-[#1c1917] text-white" : "border-[#ddd4ca] bg-white text-[#8a8178]"}`}>{n}</div>
              <div className={`mt-2 text-center text-[11px] leading-tight sm:text-xs ${step === n ? "text-[#1c1917]" : "text-[#8a8178]"}`}>{label}</div>
            </div>
            {i < steps.length - 1 && <div className={`mt-4 h-px w-6 sm:w-16 ${step > n ? "bg-[#1c1917]" : "bg-[#e4ddd4]"}`} />}
          </li>
        );
      })}
    </ol>
  );
}

function Header({ view, onNav, onRestart, brand }) {
  const [open, setOpen] = useState(false);
  const links = [
    { id: "home", label: "Beranda" },
    { id: "upload", label: "Analisa Wajah" },
    { id: "inspire", label: "Inspirasi" },
    { id: "about", label: "Tentang" },
  ];
  const go = (id) => {
    setOpen(false);
    onNav(id);
  };
  return (
    <header className="sticky top-0 z-30 border-b border-[#ece6de] bg-[#f6f3ee]/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4 sm:px-6">
        <button type="button" onClick={() => go("home")} className="text-left" data-testid="logo">
          <div className="text-[22px] leading-none tracking-[0.18em] text-[#1c1917]" style={{ fontFamily: serif }}>{brand?.name || "MIJI"}</div>
          <div className="mt-0.5 text-[9px] tracking-[0.22em] text-[#8a8178]">{brand?.sub || "HAIRCARE SALON"}</div>
        </button>
        <nav className="ml-auto hidden items-center gap-7 text-sm text-[#4a433c] md:flex">
          {links.map((l) => (
            <button key={l.id} type="button" onClick={() => go(l.id)} className={view === l.id ? "text-[#1c1917]" : "hover:text-[#1c1917]"} data-testid={`nav-${l.id}`}>
              {l.label}
            </button>
          ))}
        </nav>
        <div className="ml-auto md:ml-0">
          <Btn onClick={onRestart} testid="header-cta" className="px-3 text-[13px] sm:px-5">
            <span className="sm:hidden">Coba</span>
            <span className="hidden sm:inline">{view === "home" ? "Coba Sekarang" : "Coba Lagi"}</span>
          </Btn>
        </div>
        <button type="button" className="inline-flex h-11 w-11 items-center justify-center md:hidden" onClick={() => setOpen((v) => !v)} aria-label="Menu" data-testid="nav-menu">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {open && (
        <div className="border-t border-[#ece6de] px-4 py-3 md:hidden">
          {links.map((l) => (
            <button key={l.id} type="button" onClick={() => go(l.id)} className="block w-full rounded-lg px-2 py-3 text-left text-sm" data-testid={`nav-mobile-${l.id}`}>
              {l.label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
}

function Home({ onStart, brand }) {
  return (
    <div>
      <section className="mx-auto grid max-w-6xl items-center gap-8 px-4 py-10 sm:px-6 lg:grid-cols-2 lg:gap-12 lg:py-16">
        <div>
          <h1 className="text-[40px] leading-[1.05] text-[#1c1917] sm:text-6xl" style={{ fontFamily: serif }} data-testid="hero-title">
            {brand?.heroTitle || "Temukan Gaya Rambut Terbaik untuk Kamu"}
          </h1>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-[#6d655e] sm:text-base">
            {brand?.heroText || "Analisa bentuk wajah dari foto. Dapatkan beberapa model rambut dan warna yang cocok, dengan tampilan yang sama untuk setiap orang — hanya hasilnya yang berubah."}
          </p>
          <Btn onClick={onStart} className="mt-7" testid="start-analysis">
            Mulai Analisa Gratis <ArrowRight size={16} />
          </Btn>
        </div>
        <img src={hairSrc("hero.jpg")} alt="Contoh gaya rambut" className="h-[420px] w-full rounded-sm object-cover object-top sm:h-[520px] lg:h-[620px]" />
      </section>

      <section className="mx-auto grid max-w-6xl gap-8 px-4 py-6 sm:grid-cols-3 sm:px-6 sm:py-10">
        {[
          ["Analisa Wajah AI", "Mengenali bentuk wajah dan proporsinya dari foto yang kamu unggah atau pilih."],
          ["Analisa Proporsi", "Membaca ciri visual — rahang, dahi, jarak mata, dan perkiraan undertone kulit."],
          ["Rekomendasi Gaya", "Menyusun model rambut dan warna yang selaras dengan bacaan itu."],
        ].map(([title, text]) => (
          <div key={title} className="flex gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#e4ddd4] text-[#1c1917]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#1c1917]" />
            </div>
            <div>
              <div className="font-medium text-[#1c1917]">{title}</div>
              <p className="mt-1 text-sm leading-relaxed text-[#6d655e]">{text}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="mx-auto grid max-w-6xl items-center gap-8 px-4 py-12 sm:px-6 lg:grid-cols-2">
        <img src={hairSrc("beauty.jpg")} alt="" className="h-72 w-full rounded-sm object-cover object-top sm:h-96" />
        <div>
          <h2 className="text-4xl leading-tight text-[#1c1917] sm:text-5xl" style={{ fontFamily: serif }}>
            Gaya yang Sesuai dengan Proporsi Kamu
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-[#6d655e] sm:text-base">
            Setiap wajah punya proporsi sendiri. Saran di sini mengikuti bacaan itu, bukan satu gaya yang sama untuk semua foto.
          </p>
          <button type="button" onClick={onStart} className="mt-6 inline-flex items-center gap-2 text-sm font-medium" data-testid="start-secondary">
            Mulai Sekarang <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </div>
  );
}

function UploadStep({
  step,
  frontSource,
  busy,
  error,
  cameraSlot,
  videoRef,
  gender,
  onGender,
  onFile,
  onOpenCamera,
  onCapture,
  onAnalyze,
}) {
  const inputRef = useRef(null);
  const cameraOn = cameraSlot === "front";
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
      <Stepper step={step} />
      <h2 className="mt-8 text-center text-4xl text-[#1c1917] sm:text-5xl" style={{ fontFamily: serif }}>Upload Foto Depan</h2>
      <p className="mx-auto mt-3 max-w-xl text-center text-sm leading-relaxed text-[#6d655e]">
        Cukup satu foto menghadap kamera. Analisa dan hasil try-on memakai foto itu apa adanya, termasuk background aslinya.
      </p>
      <div className="mx-auto mt-5 flex w-fit rounded-full border border-[#e4ddd4] bg-white p-1" data-testid="gender-picker">
        {[["wanita", "Wanita"], ["pria", "Pria"]].map(([id, label]) => (
          <button key={id} type="button" onClick={() => onGender(id)} data-testid={`gender-${id}`}
            className={`min-h-11 rounded-full px-6 text-sm ${gender === id ? "bg-[#1c1917] text-white" : "text-[#4a433c]"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-8 grid items-start gap-8 lg:grid-cols-[1.15fr_0.85fr]">
        <button
          type="button"
          onClick={() => !cameraOn && inputRef.current?.click()}
          className="relative overflow-hidden rounded-[2rem] bg-[#efeae4] text-left"
          data-testid="upload-front"
        >
          {cameraOn ? (
            <video ref={videoRef} playsInline muted autoPlay className="aspect-[3/4] h-full w-full object-cover" style={{ transform: "none", WebkitTransform: "none" }} />
          ) : frontSource ? (
            <img src={frontSource} alt="Foto depan" className="aspect-[3/4] h-full w-full object-cover object-[center_18%]" />
          ) : (
            <span className="flex aspect-[3/4] w-full flex-col items-center justify-center px-8 text-center text-[#8a8178]">
              <Upload size={26} />
              <span className="mt-4 text-lg text-[#1c1917]" style={{ fontFamily: serif }}>Pilih foto depan</span>
              <span className="mt-2 text-xs">JPG, PNG, atau WEBP · maks. 10MB</span>
            </span>
          )}
          {frontSource && !cameraOn && (
            <span className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 text-white"><Check size={15} /></span>
          )}
        </button>
        <div>
          <h3 className="text-3xl text-[#1c1917]" style={{ fontFamily: serif }}>Foto lookbook, bukan paspor</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#6d655e]">
            Ambil atau unggah foto yang sudah ada. Jangan crop terlalu rapat ke wajah. Biarkan bahu, pakaian, dan background ikut terlihat.
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid="file-front"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onFile("front", file);
              event.target.value = "";
            }}
          />
          <div className="mt-6 space-y-3">
            {cameraOn ? (
              <Btn onClick={() => onCapture("front")} disabled={busy} className="w-full" testid="capture-front">
                <Camera size={16} /> Pakai Foto Ini
              </Btn>
            ) : (
              <Btn variant="ghost" onClick={() => onOpenCamera("front")} disabled={busy} className="w-full" testid="camera-front">
                <Camera size={16} /> Ambil dengan Kamera
              </Btn>
            )}
            <Btn onClick={onAnalyze} disabled={busy || !frontSource} testid="analyze-photos" className="w-full">
              <Sparkles size={16} /> {busy ? "Membaca foto…" : "Analisa Wajah"}
            </Btn>
          </div>
          <p className="mt-4 min-h-5 text-sm text-rose-700" data-testid="upload-status">{error}</p>
          <div className="mt-6 grid gap-3 text-xs text-[#6d655e]">
            <div className="rounded-2xl bg-white p-4"><strong className="block text-[#1c1917]">Lurus ke kamera</strong><span className="mt-1 block">Wajah menghadap depan, kepala tegak, rambut dan rahang terlihat.</span></div>
            <div className="rounded-2xl bg-white p-4"><strong className="block text-[#1c1917]">Cahaya merata</strong><span className="mt-1 block">Hindari bayangan keras dan backlight.</span></div>
            <div className="rounded-2xl bg-white p-4"><strong className="block text-[#1c1917]">Tanpa mirror</strong><span className="mt-1 block">Preview dan hasil kamera tidak dibalik.</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FaceMap({ photoUrl, points, width, height }) {
  if (!photoUrl || !points || !width || !height) {
    return photoUrl ? <img src={photoUrl} alt="" className="aspect-[4/5] w-full object-cover object-top" /> : null;
  }
  const brow = { x: (points[19].x + points[24].x) / 2, y: (points[19].y + points[24].y) / 2 };
  const chin = points[8];
  const cx = (points[0].x + points[16].x) / 2;
  const rx = Math.max(20, (points[16].x - points[0].x) / 2) * 1.08;
  const ry = Math.max(20, chin.y - brow.y) * 0.62;
  const cy = brow.y + (chin.y - brow.y) * 0.48;
  const pct = (p) => ({ left: `${(p.x / width) * 100}%`, top: `${(p.y / height) * 100}%` });
  const labels = [
    ["Bentuk Alis", points[19], "-translate-x-full -translate-y-1/2 pr-2 text-right"],
    ["Bentuk Mata", points[36], "-translate-x-full -translate-y-1/2 pr-2 text-right"],
    ["Bentuk Hidung", points[30], "-translate-x-full -translate-y-1/2 pr-2 text-right"],
    ["Bentuk Bibir", points[48], "-translate-x-full translate-y-1 pr-2 text-right"],
    ["Proporsi Wajah", { x: cx + rx, y: brow.y }, "translate-x-1 -translate-y-1/2"],
    ["Garis Rahang", points[12], "translate-x-1 -translate-y-1/2"],
  ];
  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#efeae4]" data-testid="face-mesh">
      <img src={photoUrl} alt="Wajah yang dianalisa" className="aspect-[4/5] w-full object-cover object-[center_18%]" />
      <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox={`0 0 ${width} ${height}`}>
        <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="none" stroke="rgba(255,255,255,0.85)" strokeDasharray="5 6" strokeWidth={Math.max(1.5, width / 420)} />
        <line x1={cx} y1={brow.y - ry * 0.35} x2={cx} y2={chin.y + 8} stroke="rgba(255,255,255,0.55)" strokeWidth={Math.max(1, width / 700)} />
        <line x1={cx - rx} y1={(points[36].y + points[45].y) / 2} x2={cx + rx} y2={(points[42].y + points[45].y) / 2} stroke="rgba(255,255,255,0.55)" strokeWidth={Math.max(1, width / 700)} />
      </svg>
      {labels.map(([text, point, cls]) => (
        <span key={text} className={`absolute hidden whitespace-nowrap text-[11px] text-white drop-shadow sm:block ${cls}`} style={pct(point)}>
          {text}
        </span>
      ))}
    </div>
  );
}

function FeatureRow({ icon, title, name, text }) {
  return (
    <div className="flex gap-3 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f6f3ee] text-[#1c1917]">{icon}</div>
      <div>
        <div className="text-sm font-medium">{title}{name ? `: ${name}` : ""}</div>
        <p className="mt-0.5 text-xs leading-relaxed text-[#6d655e]">{text}</p>
      </div>
    </div>
  );
}

function LookbookCard({ src, title, detail, index, selected, onSelect, featured = false, testid }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group relative block w-full overflow-hidden rounded-[2rem] text-left ${selected ? "ring-2 ring-[#1c1917] ring-offset-2 ring-offset-[#f6f3ee]" : ""}`}
      data-testid={testid}
    >
      <img
        src={src}
        alt={title}
        className={`w-full object-cover object-top transition duration-500 group-hover:scale-[1.03] ${featured ? "aspect-[3/4] min-h-[28rem]" : "aspect-[3/4]"}`}
      />
      <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent px-5 pb-5 pt-16">
        <span className="block text-xl text-white sm:text-2xl" style={{ fontFamily: serif }}>{index + 1}. {title}</span>
        {detail && <span className="mt-1 block text-[11px] leading-relaxed text-white/75">{detail}</span>}
      </span>
      <span className={`absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full ${selected ? "bg-white text-[#1c1917]" : "bg-black/35 text-white"}`}>
        {selected ? <Check size={15} /> : index + 1}
      </span>
    </button>
  );
}

function ResultStep({
  reading,
  photoUrl,
  points,
  width,
  height,
  gender,
  onGender,
  colors,
  families,
  activeFamilyId,
  activeStyle,
  onFamily,
  onSelectStyle,
  onApply,
  onApplyAll,
  onColor,
  allowApplyAll,
  sheetBusy,
  sheetError,
  styleResults,
  selectedStyleId,
  colorResults,
}) {
  if (!reading) return null;
  const features = reading.features || {};
  const ai = reading.ai;
  const guide = reading.colorGuide || { suggest: "", avoid: "", avoidSwatches: [] };
  const swatches = reading.palette?.length ? reading.palette : ["#E7C8A8", "#D7A882", "#C48B6A", "#A97155", "#8C5A42"];
  const activeFamily = families.find((family) => family.id === activeFamilyId);
  const variantCount = activeFamily?.variants?.length || 0;
  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-4xl text-[#1c1917] sm:text-5xl" style={{ fontFamily: serif }}>Hasil Analisa Wajah Kamu</h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#6d655e]">
            Perkiraan bentuk, ciri visual, dan warna kulit dari foto kamu. Rekomendasi gaya dan warna mengikuti bacaan itu.
          </p>
        </div>
        <Stepper step={3} />
      </div>

      <div className="mt-6 flex w-fit rounded-full border border-[#e4ddd4] bg-white p-1" data-testid="result-gender">
        {[["wanita", "Wanita"], ["pria", "Pria"]].map(([id, label]) => (
          <button key={id} type="button" onClick={() => onGender(id)} data-testid={`result-gender-${id}`}
            className={`min-h-10 rounded-full px-5 text-sm ${gender === id ? "bg-[#1c1917] text-white" : "text-[#4a433c]"}`}>
            Gaya {label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <section className="rounded-3xl border border-[#ece6de] bg-white p-4 sm:p-6">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium"><Sparkles size={16} /> Analisa Visual</div>
          <p className="mb-4 text-xs leading-relaxed text-[#8a8178]">Titik-titik ini menandai bagian yang dibaca. Wajah tidak digambar ulang.</p>
          <FaceMap photoUrl={photoUrl} points={points} width={width} height={height} />
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-[#6d655e] sm:hidden">
            {["Bentuk Alis", "Bentuk Mata", "Bentuk Hidung", "Bentuk Bibir", "Proporsi Wajah", "Garis Rahang"].map((item) => (
              <div key={item}>{item}</div>
            ))}
          </div>
        </section>

        <div className="flex flex-col gap-4">
          <section className="rounded-3xl border border-[#ece6de] bg-white p-5 sm:p-6">
            <div className="text-sm font-medium">Profil Wajah</div>
            <div className="mt-4 text-xs text-[#8a8178]">Bentuk Wajah</div>
            <div className="text-5xl text-[#1c1917]" style={{ fontFamily: serif }} data-testid="face-shape">{reading.shapeName}</div>
            {ai?.shape ? (
              <div className="mt-2 flex gap-4 text-xs text-[#6d655e]">
                <span>{ai.shape.primary}: {ai.shape.primaryPct}%</span>
                <span>{ai.shape.secondary}: {ai.shape.secondaryPct}%</span>
              </div>
            ) : (
              <div className="mt-1 text-xs text-[#8a8178]">Keyakinan geometri {Math.round((reading.shapeConfidence || 0.5) * 100)}% · bukan penilaian karakter pribadi</div>
            )}
            <p className="mt-2 text-sm leading-relaxed text-[#4a433c]" data-testid="face-summary">{reading.summary}</p>
            <p className="mt-3 rounded-2xl bg-[#f6f3ee] px-4 py-3 text-sm leading-relaxed text-[#4a433c]">{reading.shapeAdvice}</p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="character-list">
              {(reading.visualTraits || reading.characters).map((c) => (
                <span key={c} className="rounded-full border border-[#e4ddd4] px-3 py-1 text-xs">{c}</span>
              ))}
            </div>
          </section>
          <section className="rounded-3xl border border-[#ece6de] bg-white p-5 sm:p-6">
            <div className="text-sm font-medium">Ciri Visual yang Menonjol</div>
            {ai?.visualStrengths?.length ? (
              <div className="mt-2 divide-y divide-[#f0ebe4]">
                {ai.visualStrengths.slice(0, 6).map((item) => (
                  <FeatureRow key={item.title} icon={<Sparkles size={15} />} title={item.title} name="" text={item.detail} />
                ))}
              </div>
            ) : (
              <div className="mt-2 divide-y divide-[#f0ebe4]">
                <FeatureRow icon={<Eye size={16} />} title="Mata" name={features.eyes?.name} text={features.eyes?.text} />
                <FeatureRow icon={<span className="text-xs">◎</span>} title="Bentuk Hidung" name={features.nose?.name} text={features.nose?.text} />
                <FeatureRow icon={<span className="text-xs">◡</span>} title="Bentuk Bibir" name={features.lips?.name} text={features.lips?.text} />
                <FeatureRow icon={<span className="text-xs">⌒</span>} title="Garis Rahang" name={features.jaw?.name} text={features.jaw?.text} />
              </div>
            )}
          </section>
        </div>
      </div>

      {ai?.structure && (
        <section className="mt-4 rounded-3xl border border-[#ece6de] bg-white p-5 sm:p-6" data-testid="ai-structure">
          <div className="text-sm font-medium">Struktur Wajah & Rambut</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              ["Dahi", ai.structure.forehead],
              ["Tulang Pipi", ai.structure.cheekbones],
              ["Rahang", ai.structure.jaw],
              ["Dagu", ai.structure.chin],
              ["Panjang Wajah", ai.structure.faceLength],
              ["Rambut Saat Ini", `${ai.hair?.density || ""} · ${ai.hair?.texture || ""}. ${ai.hair?.current || ""}`],
            ].map(([title, text]) => (
              <div key={title} className="rounded-2xl bg-[#f6f3ee] p-4">
                <div className="text-xs font-medium text-[#1c1917]">{title}</div>
                <p className="mt-1 text-xs leading-relaxed text-[#6d655e]">{text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <section className="rounded-3xl border border-[#ece6de] bg-white p-5 sm:p-6">
          <div className="text-sm font-medium">Perkiraan Skin Tone & Undertone</div>
          <p className="mt-1 text-xs text-[#8a8178]">{reading.skinNote}</p>
          <div className="mt-4 flex gap-4">
            <img src={photoUrl} alt="" className="h-28 w-28 rounded-2xl object-cover object-[center_30%]" />
            <div>
              <div className="text-3xl text-[#1c1917]" style={{ fontFamily: serif }} data-testid="skin-label">{reading.skinLabel}</div>
              <p className="mt-2 max-w-xs text-xs leading-relaxed text-[#6d655e]">
                {reading.skin?.undertone === "hangat"
                  ? "Kulit cenderung hangat. Warna cokelat, karamel, dan tembaga biasanya lebih menyatu."
                  : reading.skin?.undertone === "dingin"
                    ? "Kulit cenderung dingin. Warna ashy dan hitam alami biasanya lebih rata."
                    : "Undertone netral. Cokelat sedang dan hitam alami sama-sama bisa dipakai."}
              </p>
              <div className="mt-3 flex gap-2" data-testid="skin-swatches">
                {swatches.map((hex, i) => <span key={`${hex}-${i}`} className="h-6 w-6 rounded-full border border-black/10" style={{ background: hex }} />)}
              </div>
            </div>
          </div>
        </section>
        <section className="rounded-3xl border border-[#ece6de] bg-white p-5 sm:p-6">
          <div className="text-sm font-medium">Rekomendasi Warna Rambut</div>
          <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid="color-list">
            {colors.map((c, i) => (
              <div key={c.id} className="text-center" data-testid={`color-${c.id}`}>
                <div className="mx-auto h-14 w-10 rounded-full border border-black/10" style={{ background: `linear-gradient(180deg, ${c.hex} 0%, #1a120e 140%)` }} />
                <div className="mt-2 text-[10px] leading-tight">{c.name}</div>
                {i === 0 && <div className="mt-1 text-[10px] text-[#8a5a3a]">Paling cocok</div>}
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <section className="rounded-3xl border border-[#ece6de] bg-white p-5 sm:p-6">
          <div className="text-sm font-medium">Arah Warna yang Disarankan</div>
          <p className="mt-3 text-sm leading-relaxed text-[#4a433c]">
            {ai?.hairColors?.best?.length ? ai.hairColors.best.join(" · ") : guide.suggest}
          </p>
        </section>
        <section className="rounded-3xl border border-[#ece6de] bg-white p-5 sm:p-6">
          <div className="text-sm font-medium">Warna yang Perlu Hati-hati</div>
          <p className="mt-3 text-sm leading-relaxed text-[#4a433c]">
            {ai?.hairColors?.avoid?.length ? ai.hairColors.avoid.join(" · ") : guide.avoid}
          </p>
          <div className="mt-4 grid grid-cols-4 gap-3">
            {(guide.avoidSwatches || []).map((c) => (
              <div key={c.id} className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full border border-black/10" style={{ background: c.hex }} />
                <div className="mt-1 text-[10px] leading-tight text-[#6d655e]">{c.name}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {ai?.bestDirection && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <section className="rounded-3xl border border-[#d9cbbb] bg-[#1c1917] p-5 text-white sm:p-6" data-testid="best-direction">
            <div className="text-xs uppercase tracking-[0.18em] text-white/60">Arah Rambut Paling Cocok</div>
            <h3 className="mt-2 text-3xl" style={{ fontFamily: serif }}>{ai.bestDirection.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-white/80">{ai.bestDirection.why}</p>
            <div className="mt-4 space-y-2">
              {(ai.bestDirection.stylistNotes || []).map((note) => (
                <div key={note} className="flex gap-2 text-xs leading-relaxed text-white/75">
                  <Check size={14} className="mt-0.5 shrink-0" /> {note}
                </div>
              ))}
            </div>
          </section>
          <section className="rounded-3xl border border-[#ece6de] bg-white p-5 sm:p-6" data-testid="avoid-haircuts">
            <div className="text-sm font-medium">Yang Sebaiknya Dihindari</div>
            <div className="mt-3 divide-y divide-[#f0ebe4]">
              {(ai.avoid || []).map((item) => (
                <div key={item.name} className="py-3">
                  <div className="text-sm font-medium">{item.name}</div>
                  <p className="mt-1 text-xs leading-relaxed text-[#6d655e]">{item.reason}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      <section className="mt-10" data-testid="style-families">
        <h3 className="text-3xl text-[#1c1917] sm:text-4xl" style={{ fontFamily: serif }}>
          {families.length} Keluarga Style Paling Cocok
        </h3>
        <p className="mt-2 max-w-2xl text-sm text-[#6d655e]">
          Pilih arah style terlebih dahulu. Jumlah kartu mengikuti catalog, tanpa kartu pengisi.
          Nama dan gambar kartu sama dengan gaya SnapEdit yang dipasang.
        </p>
        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {families.map((family, index) => {
            const aiRecommendation = (ai?.recommendations || []).find((item) => item.id === family.id);
            const selected = activeFamilyId === family.id;
            return (
              <button
                key={family.id}
                type="button"
                onClick={() => onFamily(family.id)}
                className={`flex items-center gap-4 rounded-3xl border bg-white p-3 text-left ${selected ? "border-[#1c1917] ring-1 ring-[#1c1917]" : "border-[#ece6de]"}`}
                data-testid={`family-${family.id}`}
              >
                <img src={styleVariantImage(family.variants[0])} alt="" className="h-24 w-20 rounded-2xl object-cover object-top" />
                <span>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-[#8a8178]">Rekomendasi {index + 1}</span>
                  <span className="mt-1 block text-xl text-[#1c1917]" style={{ fontFamily: serif }}>{family.name}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-[#6d655e]">{aiRecommendation?.reason || family.summary}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-8 flex items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-[#8a8178]">
              {variantCount} model dalam satu keluarga
            </div>
            <h4 className="mt-1 text-3xl text-[#1c1917]" style={{ fontFamily: serif }}>
              {variantCount} Varian {activeFamily?.name}
            </h4>
          </div>
          <div className="hidden text-xs text-[#8a8178] sm:block">Preview gaya yang dipasang</div>
        </div>
        <div className={`mt-4 ${variantGridClass(variantCount)}`}>
          {(activeFamily?.variants || []).map((style, index) => {
            const selected = selectedStyleId === style.id;
            return (
              <button
                key={style.id}
                type="button"
                onClick={() => onSelectStyle(style.id)}
                className={`overflow-hidden rounded-2xl border bg-white p-2 text-left ${selected ? "border-[#1c1917] ring-1 ring-[#1c1917]" : "border-[#ece6de]"}`}
                data-testid={`variant-${style.id}`}
              >
                <span className="relative block aspect-[4/5] overflow-hidden rounded-xl bg-[#eee8e1]">
                  <img src={styleVariantImage(style)} alt={style.name} className="h-full w-full object-cover object-top" />
                  <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full border border-white/80 bg-white/85 text-xs">{index + 1}</span>
                </span>
                <span className="mt-3 block text-base leading-tight text-[#1c1917]" style={{ fontFamily: serif }}>{style.name}</span>
                <span className="mt-1 block text-[10px] leading-relaxed text-[#6d655e]">{style.detail}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Btn onClick={onApply} disabled={sheetBusy === "styles" || !activeStyle} testid="apply-style">
            {sheetBusy === "styles" ? "Memasang model ke foto…" : `Aplikasikan ${activeStyle?.name || "varian terpilih"}`}
          </Btn>
          {allowApplyAll && variantCount > 1 && (
            <Btn variant="ghost" onClick={onApplyAll} disabled={sheetBusy === "styles"} testid="apply-all-styles">
              Aplikasikan semua {variantCount}
            </Btn>
          )}
        </div>
        <p className="mt-2 text-xs text-[#8a8178]">
          Try-on memakai gaya catalog, bukan copy lookbook. Setiap kartu yang tampil dipasang terpisah.
          Apply satu model tetap ada jika hanya mau coba satu.
        </p>
        {sheetError && <p className="mt-3 text-sm text-[#8a5a3a]" data-testid="sheet-error">{sheetError}</p>}
      </section>

      {styleResults && (
        <section className="mt-10" data-testid="style-results">
          <div className="mb-5">
            <div className="text-xs uppercase tracking-[0.16em] text-[#8a8178]">Hasil aplikasi</div>
            <h3 className="mt-1 text-3xl text-[#1c1917]" style={{ fontFamily: serif }}>Hasil di Foto Kamu</h3>
            <p className="mt-2 text-sm text-[#6d655e]">Setiap varian dibuat terpisah. Pilih salah satu, lalu warna dipasang satu per satu.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {(families.find((family) => family.id === activeFamilyId)?.variants || [])
              .filter((style) => styleResults[style.id])
              .map((style, index) => (
                <LookbookCard
                  key={style.id}
                  src={styleResults[style.id]}
                  title={style.name}
                  detail={style.detail}
                  index={index}
                  selected={selectedStyleId === style.id}
                  featured={index === 0 && Object.keys(styleResults).length === 1}
                  onSelect={() => onSelectStyle(style.id)}
                  testid={`result-${style.id}`}
                />
              ))}
          </div>
        </section>
      )}

      {styleResults && activeStyle && styleResults[activeStyle.id] && (
        <section className="mt-8" data-testid="color-apply">
          <h3 className="text-3xl text-[#1c1917]" style={{ fontFamily: serif }}>Warna untuk {activeStyle?.name}</h3>
          <p className="mt-2 text-sm text-[#6d655e]">Klik satu warna untuk memasangnya. Tidak perlu membuat keenamnya sekaligus.</p>
          <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
            {colors.slice(0, 6).map((color) => (
              <button
                key={color.id}
                type="button"
                onClick={() => onColor(color.hex)}
                disabled={sheetBusy === "colors"}
                className="text-center"
                data-testid={`apply-color-${color.id}`}
              >
                <div className="mx-auto h-12 w-8 rounded-full border border-black/10" style={{ background: color.hex }} />
                <div className="mt-1 text-[10px] leading-tight">{color.name}</div>
              </button>
            ))}
          </div>
          {sheetBusy === "colors" && <p className="mt-3 text-sm text-[#6d655e]">Memasang warna…</p>}
          {colorResults && Object.keys(colorResults).length > 0 && (
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="color-sheet">
              {colors.slice(0, 6).filter((color) => colorResults[color.hex]).map((color, index) => (
                <figure key={color.id} className="overflow-hidden rounded-[1.75rem]">
                  <img src={colorResults[color.hex]} alt={color.name} className="aspect-[3/4] w-full object-cover object-[center_16%]" />
                  <figcaption className="mt-2 flex items-center gap-2 text-xs text-[#4a433c]">
                    <span className="h-3 w-3 rounded-full border border-black/10" style={{ background: color.hex }} />
                    {index + 1}. {color.name}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function HairLook({ photoUrl, points, style, color, fallbackSrc, className }) {
  const [src, setSrc] = useState(fallbackSrc);
  const [state, setState] = useState("idle");
  useEffect(() => {
    if (!photoUrl || !points || !style || !color) {
      setSrc(fallbackSrc);
      setState("idle");
      return undefined;
    }
    let cancel = false;
    setSrc(photoUrl);
    setState("wait");
    const timer = setTimeout(() => {
      requestHairEdit({ photoUrl, points, style, color })
        .then((url) => {
          if (!cancel) {
            setSrc(url);
            setState("done");
          }
        })
        .catch(() => {
          if (!cancel) {
            setSrc(photoUrl);
            setState("error");
          }
        });
    }, 50);
    return () => {
      cancel = true;
      clearTimeout(timer);
    };
  }, [photoUrl, points, style, color, fallbackSrc]);
  return (
    <div className="relative">
      <img src={src} alt="" className={className} />
      {state === "wait" && (
        <span className="absolute bottom-3 left-3 rounded-full bg-[#f6f3ee]/95 px-3 py-1 text-xs text-[#1c1917]">Memasang rambut…</span>
      )}
      {state === "error" && (
        <span className="absolute bottom-3 left-3 rounded-full bg-[#f6f3ee]/95 px-3 py-1 text-xs text-[#1c1917]">Rambut belum terpasang</span>
      )}
    </div>
  );
}

function StyleCard({ style, reading, onOpen, look }) {
  return (
    <article className="flex h-full flex-col" data-testid={`style-${style.id}`}>
      <img src={refImage(style, look)} alt="" className="aspect-[4/5] w-full rounded-xl object-cover object-top" />
      <h3 className="mt-4 text-2xl text-[#1c1917]" style={{ fontFamily: serif }}>{style.name}</h3>
      <div className="mt-1 text-xs text-[#8a8178]">{style.tags.join(" · ")}</div>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-[#4a433c]">{styleReason(style, reading)}</p>
      <Btn variant="ghost" onClick={onOpen} className="mt-4 w-full" testid={`open-${style.id}`}>Pilih gaya ini</Btn>
    </article>
  );
}

function RecommendStep({ reading, gender, filter, setFilter, onOpen, onBook, look }) {
  const styles = useMemo(() => {
    const ranked = recommendStyles(reading, gender).filter((style) => hasRef(style, look));
    if (filter === "semua") return ranked;
    return ranked.filter((s) => s.filters.includes(filter) || s.length === filter);
  }, [reading, gender, filter, look]);
  const scroller = useRef(null);
  const scrollBy = (dir) => scroller.current?.scrollBy({ left: dir * (scroller.current.clientWidth * 0.8), behavior: "smooth" });

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
      <Stepper step={3} />
      <h2 className="mt-8 text-center text-4xl text-[#1c1917] sm:text-5xl" style={{ fontFamily: serif }}>Rekomendasi Gaya Rambut</h2>
      <p className="mx-auto mt-3 max-w-lg text-center text-sm leading-relaxed text-[#6d655e]">
        {reading ? `Referensi untuk wajah ${reading.shapeName.toLowerCase()}. Ini contoh potongan, belum dipasang ke foto kamu.` : "Pilih model untuk melihat detail. Analisa wajah akan mengurutkan yang paling cocok."}
      </p>
      <div className="mt-6 flex gap-2 overflow-x-auto pb-2" data-testid="style-filters">
        {FILTERS.map((f) => (
          <button key={f.id} type="button" onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}
            className={`shrink-0 rounded-full px-4 py-2 text-sm ${filter === f.id ? "bg-[#1c1917] text-white" : "border border-[#e4ddd4] bg-white text-[#4a433c]"}`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="relative mt-4">
        <button type="button" aria-label="Sebelumnya" onClick={() => scrollBy(-1)} className="absolute -left-1 top-1/3 z-10 hidden h-10 w-10 items-center justify-center rounded-full border border-[#e4ddd4] bg-white sm:flex"><ArrowLeft size={16} /></button>
        <div ref={scroller} className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 sm:grid sm:grid-cols-2 sm:overflow-visible lg:grid-cols-3">
          {styles.map((style) => (
            <div key={style.id} className="w-[82%] shrink-0 snap-center sm:w-auto">
              <StyleCard style={style} reading={reading} onOpen={() => onOpen(style.id)} look={look} />
            </div>
          ))}
        </div>
        <button type="button" aria-label="Berikutnya" onClick={() => scrollBy(1)} className="absolute -right-1 top-1/3 z-10 hidden h-10 w-10 items-center justify-center rounded-full border border-[#e4ddd4] bg-white sm:flex"><ArrowRight size={16} /></button>
      </div>
      {!styles.length && <p className="py-10 text-center text-sm text-[#6d655e]">Tidak ada model di filter ini.</p>}
      <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-2xl border border-[#ece6de] bg-white px-5 py-5 sm:flex-row sm:items-center">
        <div>
          <div className="font-medium">Konsultasikan dengan Stylist Kami</div>
          <p className="mt-1 text-sm text-[#6d655e]">Saran di layar ini bahan percakapan. Stylist yang menyesuaikan potongan di salon.</p>
        </div>
        <Btn onClick={onBook} testid="book-consult" className="w-full sm:w-auto">Booking Konsultasi</Btn>
      </div>
    </div>
  );
}

function DetailStep({ style, reading, colors, onBack, onTry, photoUrl, points, color }) {
  if (!style) return null;
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#1c1614] text-white">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-10">
        <button type="button" onClick={onBack} className="inline-flex min-h-11 items-center gap-2 text-sm text-white/80" data-testid="detail-back">
          <ArrowLeft size={16} /> Kembali
        </button>
        <div className="mt-4 grid items-center gap-8 lg:grid-cols-2">
          {photoUrl && points && color ? (
            <HairLook photoUrl={photoUrl} points={points} style={style} color={color} fallbackSrc={hairSrc(style.image)} className="aspect-[4/5] w-full rounded-2xl object-cover object-top" />
          ) : (
            <img src={hairSrc(style.image)} alt="" className="aspect-[4/5] w-full rounded-2xl object-cover object-top" />
          )}
          <div>
            <h2 className="text-5xl sm:text-6xl" style={{ fontFamily: serif }} data-testid="detail-name">{style.name}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {style.tags.map((t) => <span key={t} className="rounded-full border border-white/20 px-3 py-1 text-xs">{t}</span>)}
            </div>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-white/75">{style.detail}</p>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-white/75">{styleReason(style, reading)}</p>
            <Btn variant="light" onClick={onTry} className="mt-6" testid="try-style">Coba di Wajah Saya</Btn>
          </div>
        </div>
        <div className="mt-10">
          <div className="text-sm text-white/70">Warna yang Direkomendasikan</div>
          <div className="mt-4 grid grid-cols-3 gap-4 sm:grid-cols-5" data-testid="color-list">
            {colors.map((c) => (
              <div key={c.id} className="text-center" data-testid={`color-${c.id}`}>
                <div className="mx-auto h-14 w-14 rounded-full border border-white/10" style={{ background: c.hex }} />
                <div className="mt-2 text-xs">{c.name}</div>
                <div className="mt-1 text-[11px] leading-snug text-white/50">{colorReason(c, reading?.skin)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TryStep({ style, styles, photoUrl, points, colors, skin, onPick, onSave, onShare, notice, onBack }) {
  const variants = (colors || []).slice(0, 4);
  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
      <button type="button" onClick={onBack} className="inline-flex min-h-11 items-center gap-2 text-sm text-[#4a433c]" data-testid="try-back">
        <ArrowLeft size={16} /> Kembali ke analisa
      </button>
      <h2 className="mt-4 text-4xl text-[#1c1917] sm:text-5xl" style={{ fontFamily: serif }}>{style?.name || "Coba Gaya"}</h2>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#6d655e]">
        Empat warna yang paling cocok dipasang bersamaan di foto kamu. Wajah tetap dikunci. Hasil pertama biasanya muncul dalam belasan detik, sisanya menyusul bareng.
      </p>
      <div className="mt-5 flex gap-3 overflow-x-auto pb-2">
        {styles.map((s) => (
          <button key={s.id} type="button" onClick={() => onPick(s.id)} data-testid={`try-${s.id}`}
            className={`w-16 shrink-0 ${style?.id === s.id ? "opacity-100" : "opacity-60"}`}>
            <img src={hairSrc(s.image)} alt="" className={`aspect-[4/5] w-full rounded-lg object-cover object-top ${style?.id === s.id ? "ring-2 ring-[#1c1917]" : ""}`} />
            <div className="mt-1 truncate text-[10px]">{s.name}</div>
          </button>
        ))}
      </div>
      {photoUrl && points && style && variants.length > 0 ? (
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="try-colors">
          {variants.map((c) => (
            <figure key={c.id} data-testid={`try-color-${c.id}`}>
              <HairLook photoUrl={photoUrl} points={points} style={style} color={c.hex} fallbackSrc={photoUrl} className="aspect-[4/5] w-full rounded-2xl object-cover object-top" />
              <figcaption className="mt-2 flex items-center justify-center gap-2 text-xs text-[#4a433c]">
                <span className="h-3 w-3 rounded-full border border-black/10" style={{ background: c.hex }} />
                {c.name}
              </figcaption>
              <p className="mt-1 text-center text-[11px] leading-snug text-[#8a8178]">{colorReason(c, skin)}</p>
            </figure>
          ))}
        </div>
      ) : (
        <p className="mt-8 text-sm text-[#6d655e]">Unggah foto dulu supaya warna bisa dipasang di wajah.</p>
      )}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <Btn variant="ghost" onClick={onSave} testid="save-result" className="w-full sm:w-auto">Simpan Hasil</Btn>
        <Btn onClick={onShare} testid="share-result" className="w-full sm:w-auto">Bagikan</Btn>
      </div>
      {notice && <p className="mt-3 text-sm text-[#6d655e]" data-testid="share-notice">{notice}</p>}
    </div>
  );
}

function About({ brand }) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-14 sm:px-6">
      <h2 className="text-5xl text-[#1c1917]" style={{ fontFamily: serif }}>Tentang</h2>
      <p className="mt-4 text-sm leading-relaxed text-[#4a433c] sm:text-base">
        {brand?.about || "Halaman ini membaca foto wajah di perangkat kamu, lalu menyusun model rambut dan warna yang mengikuti bentuk wajah itu. Tata letaknya sama untuk setiap pengunjung. Yang berubah hanya bacaan, urutan model, dan warna."}
      </p>
      <p className="mt-3 text-sm leading-relaxed text-[#6d655e]">
        Ini saran gaya, bukan diagnosis. Foto miring, gelap, atau tertutup kacamata membuat bacaan kurang stabil.
      </p>
    </div>
  );
}

function Booking({ onClose, brand }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-[#f6f3ee] p-6" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
        <h3 className="text-4xl text-[#1c1917]" style={{ fontFamily: serif }}>Booking Konsultasi</h3>
        <p className="mt-3 text-sm leading-relaxed text-[#4a433c]">
          {brand?.bookingNote || "Pratinjau ini belum mengirim janji ke salon. Bawa hasil analisa saat datang, stylist yang menentukan potongan akhirnya."}
        </p>
        <Btn onClick={onClose} className="mt-6 w-full">Tutup</Btn>
      </div>
    </div>
  );
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Gambar tidak bisa dibuka."));
    img.src = src;
  });
}

function fitCanvas(img, maxSide = 960) {
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function lerp(a, b, t) {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function sampleSkin(canvas, pts) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const brow = {
    x: (pts[19].x + pts[24].x) / 2,
    y: (pts[19].y + pts[24].y) / 2,
  };
  const spots = [
    lerp(pts[31], pts[3], 0.42),
    lerp(pts[35], pts[13], 0.42),
    { x: brow.x, y: brow.y - 8 },
    lerp(pts[30], pts[33], 0.45),
  ];
  const buckets = [];
  for (const spot of spots) {
    const x = Math.round(spot.x);
    const y = Math.round(spot.y);
    const radius = 5;
    if (x < radius || y < radius || x >= canvas.width - radius || y >= canvas.height - radius) continue;
    const data = ctx.getImageData(x - radius, y - radius, radius * 2 + 1, radius * 2 + 1).data;
    const pixels = [];
    for (let i = 0; i < data.length; i += 4) {
      const rr = data[i]; const gg = data[i + 1]; const bb = data[i + 2];
      const L = 0.299 * rr + 0.587 * gg + 0.114 * bb;
      const spread = Math.max(rr, gg, bb) - Math.min(rr, gg, bb);
      if (L < 45 || L > 235 || spread > 115) continue;
      pixels.push({ r: rr, g: gg, b: bb });
    }
    if (!pixels.length) continue;
    const median = (key) => pixels.map((pixel) => pixel[key]).sort((a, b) => a - b)[Math.floor(pixels.length / 2)];
    buckets.push({ r: median("r"), g: median("g"), b: median("b") });
  }
  if (!buckets.length) return null;
  const medianBucket = (key) => buckets.map((bucket) => bucket[key]).sort((a, b) => a - b)[Math.floor(buckets.length / 2)];
  const hex = (c) => `#${[c.r, c.g, c.b].map((n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0")).join("")}`;
  return {
    r: medianBucket("r"),
    g: medianBucket("g"),
    b: medianBucket("b"),
    palette: buckets.map(hex),
  };
}

async function readPhoto(src) {
  const img = await loadImage(src);
  const canvas = fitCanvas(img);
  const faces = await detectFaceForReading(canvas);
  if (!faces.length) {
    const err = new Error("Wajah tidak terbaca. Pilih foto menghadap kamera, terang, tanpa kacamata.");
    err.preview = canvas.toDataURL("image/jpeg", 0.86);
    throw err;
  }
  const face = faces.reduce((a, b) => ((a.detection.box.width * a.detection.box.height) > (b.detection.box.width * b.detection.box.height) ? a : b));
  const pts = face.landmarks.positions;
  const metrics = measureLandmarks(pts);
  const faceCoverage = face.detection.box.width / canvas.width;
  if (
    Math.abs(metrics.roll || 0) > 15
    || Math.abs(metrics.yaw || 0) > 0.18
    || (metrics.asymmetry || 0) > 0.25
    || faceCoverage < 0.12
  ) {
    const err = new Error("Foto kurang frontal atau wajah terlalu kecil. Gunakan foto lurus menghadap kamera agar bentuk wajah tidak salah.");
    err.preview = canvas.toDataURL("image/jpeg", 0.9);
    throw err;
  }
  const sampled = sampleSkin(canvas, pts);
  const skin = sampled ? readSkinTone(sampled) : null;
  const reading = buildReading(metrics, skin);
  if (sampled?.palette) reading.palette = sampled.palette;
  const clean = canvas.toDataURL("image/jpeg", 0.86);
  const points = pts.map((p) => ({ x: p.x, y: p.y }));
  return { photoUrl: clean, reading, points, width: canvas.width, height: canvas.height };
}

function mergeAiAnalysis(reading, ai) {
  if (!ai?.shape) return reading;
  const validShapes = new Set(["oval", "round", "square", "heart", "oblong", "diamond"]);
  const primary = validShapes.has(ai.shape.primaryId) ? ai.shape.primaryId : reading.shape;
  const secondary = validShapes.has(ai.shape.secondaryId) ? ai.shape.secondaryId : reading.secondary;
  const undertoneText = String(ai.skin?.undertone || "").toLowerCase();
  const undertone = /(warm|hangat|golden|peach)/.test(undertoneText)
    ? "hangat"
    : /(cool|dingin|pink)/.test(undertoneText)
      ? "dingin"
      : "netral";
  const depthText = String(ai.skin?.depth || "").toLowerCase();
  const depth = /(deep|dark|gelap)/.test(depthText)
    ? "gelap"
    : /(tan|sawo)/.test(depthText)
      ? "sawo"
      : /(light|fair|cerah|terang)/.test(depthText)
        ? "cerah"
        : "sedang";
  const visualTraits = (ai.visualStrengths || []).slice(0, 5).map((item) => item.title).filter(Boolean);
  return {
    ...reading,
    shape: primary,
    secondary,
    shapeName: ai.shape.label || ai.shape.primary || reading.shapeName,
    shapeConfidence: Math.max(0.01, Math.min(1, Number(ai.shape.primaryPct || 50) / 100)),
    summary: ai.shape.summary || reading.summary,
    shapeAdvice: ai.bestDirection?.why || reading.shapeAdvice,
    visualTraits: visualTraits.length ? visualTraits : reading.visualTraits,
    characters: visualTraits.length ? visualTraits : reading.characters,
    skin: {
      ...reading.skin,
      undertone,
      depth,
      label: `${ai.skin?.depth || "Medium"} • ${ai.skin?.undertone || "Neutral"}`,
    },
    skinLabel: `${ai.skin?.depth || "Medium"} • ${ai.skin?.undertone || "Neutral"}`,
    skinNote: ai.skin?.note || reading.skinNote,
    aiFamilyIds: (ai.recommendations || []).map((item) => item.id).filter(Boolean),
    ai,
  };
}

export default function HairConsult() {
  useFonts();
  const [view, setView] = useState("home");
  const [step, setStep] = useState(1);
  const [frontSource, setFrontSource] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoSize, setPhotoSize] = useState({ width: 0, height: 0 });
  const [points, setPoints] = useState(null);
  const [reading, setReading] = useState(null);
  const [gender, setGender] = useState("wanita");
  const [familyId, setFamilyId] = useState("");
  const [styleResults, setStyleResults] = useState(null);
  const [colorResults, setColorResults] = useState(null);
  const [sheetBusy, setSheetBusy] = useState("");
  const [sheetError, setSheetError] = useState("");
  const [hairColor, setHairColor] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("semua");
  const [styleId, setStyleId] = useState("");
  const [booking, setBooking] = useState(false);
  const [cameraSlot, setCameraSlot] = useState("");
  const [notice, setNotice] = useState("");
  const [site, setSite] = useState(null);
  const [catalogTick, setCatalogTick] = useState(0);
  const [unlocked, setUnlocked] = useState(false);
  const [gatePassword, setGatePassword] = useState("");
  const [gateBusy, setGateBusy] = useState(false);
  const [gateError, setGateError] = useState("");
  const [gateNotice, setGateNotice] = useState("");
  const [gateForgot, setGateForgot] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, [view]);
  useEffect(() => { document.title = `${site?.brand?.name || "MIJI"} Haircare — Konsultasi Gaya Rambut`; }, [site]);
  useEffect(() => { loadFaceReadModels().catch(() => {}); }, []);
  useEffect(() => {
    let cancel = false;
    fetchPublicConfig()
      .then(async (cfg) => {
        if (cancel) return;
        setSite(cfg);
        const open = !cfg.visitorGate?.enabled || cfg.session?.visitor || cfg.session?.admin;
        setUnlocked(Boolean(open));
        if (open) {
          try {
            const data = await fetchCatalog();
            if (!cancel && data.families?.length) {
              setStyleFamilies(data.families);
              setCatalogTick((n) => n + 1);
            }
          } catch (e) {
            // Bundled catalog remains available if the API is down.
          }
        }
      })
      .catch(() => {
        if (!cancel) setUnlocked(true);
      });
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (!cameraSlot) return undefined;
    let cancel = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        if (cancel) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (e) {
        setError("Kamera tidak bisa dipakai. Unggah foto sebagai gantinya.");
        setCameraSlot("");
      }
    })();
    return () => {
      cancel = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [cameraSlot]);

  const colors = useMemo(() => {
    if (site?.colors?.length) return site.colors;
    return recommendColors(reading?.skin);
  }, [reading, site]);
  useEffect(() => {
    if (colors[0]?.hex) setHairColor(colors[0].hex);
  }, [colors]);
  const rankedFamilies = useMemo(() => recommendStyleFamilies(reading, gender), [reading, gender, catalogTick]);
  const families = useMemo(() => rankedFamilies.slice(0, 3), [rankedFamilies]);
  const activeFamily = families.find((family) => family.id === familyId) || families[0];
  const activeStyle = activeFamily?.variants.find((style) => style.id === styleId);

  useEffect(() => {
    if (!activeFamily) return;
    if (familyId !== activeFamily.id) setFamilyId(activeFamily.id);
    if (!activeFamily.variants.some((style) => style.id === styleId)) {
      setStyleId(activeFamily.variants[0].id);
    }
  }, [activeFamily, familyId, styleId]);

  const openCatalog = async () => {
    const data = await fetchCatalog();
    if (data.families?.length) {
      setStyleFamilies(data.families);
      setCatalogTick((n) => n + 1);
    }
  };

  const submitGateForgot = async (event) => {
    event.preventDefault();
    setGateBusy(true);
    setGateError("");
    setGateNotice("");
    try {
      const result = await forgotPassword({ kind: "visitor" });
      setGateNotice(result.message || "Password sent to: kli...c@g...com");
    } catch (e) {
      setGateError(e.message || "Email belum bisa dikirim.");
    } finally {
      setGateBusy(false);
    }
  };

  const submitGate = async (event) => {
    event.preventDefault();
    setGateBusy(true);
    setGateError("");
    try {
      await unlockVisitor(gatePassword);
      setUnlocked(true);
      setGatePassword("");
      await openCatalog();
    } catch (e) {
      setGateError(e.message || "Kode akses salah.");
    } finally {
      setGateBusy(false);
    }
  };

  const go = (next) => {
    setNotice("");
    setError("");
    if (next === "upload") setStep(1);
    if (next === "inspire") setView(reading ? "result" : "upload");
    else setView(next);
  };

  const restart = () => {
    setCameraSlot("");
    setFrontSource("");
    setPhotoUrl("");
    setPhotoSize({ width: 0, height: 0 });
    setPoints(null);
    setReading(null);
    setStyleResults(null);
    setColorResults(null);
    setFamilyId("");
    setStyleId("");
    setSheetBusy("");
    setSheetError("");
    setHairColor("");
    setError("");
    setStep(1);
    setFilter("semua");
    setView("upload");
  };

  const analyzePhotos = async () => {
    if (!frontSource) {
      setError("Foto depan wajib diisi.");
      return;
    }
    setBusy(true);
    setError("");
    setStep(2);
    setCameraSlot("");
    try {
      const result = await readPhoto(frontSource);
      try {
        const ai = await requestFaceAnalysis(result.photoUrl, gender);
        result.reading = mergeAiAnalysis(result.reading, ai);
      } catch (analysisError) {
        // The local landmark reading remains available as a fallback.
      }
      setPhotoUrl(result.photoUrl);
      setPhotoSize({ width: result.width, height: result.height });
      setPoints(result.points);
      setReading(result.reading);
      setStyleResults(null);
      setColorResults(null);
      setSheetError("");
      const firstFamily = recommendStyleFamilies(result.reading, gender)[0];
      setFamilyId(firstFamily?.id || "");
      setStyleId("");
      setView("result");
    } catch (e) {
      setError(e.message || "Foto tidak bisa dibaca.");
      setStep(1);
      setView("upload");
    } finally {
      setBusy(false);
    }
  };

  const onFile = (slot, file) => {
    if (!file.type.startsWith("image/")) {
      setError("Pilih file gambar.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Ukuran foto maksimal 10MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFrontSource(reader.result);
      setError("");
      setReading(null);
      setStyleResults(null);
      setColorResults(null);
    };
    reader.readAsDataURL(file);
  };

  const onCapture = (slot) => {
    const video = videoRef.current;
    const canvas = snapshotVideo(video, { maxSide: 960 });
    if (!canvas) {
      setError("Kamera belum siap.");
      return;
    }
    const src = canvas.toDataURL("image/jpeg", 0.9);
    setFrontSource(src);
    setCameraSlot("");
    setError("");
    setReading(null);
    setStyleResults(null);
    setColorResults(null);
  };

  const saveResult = () => {
    if (!reading) {
      setNotice("Analisa wajah dulu, baru hasilnya bisa disimpan.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = 900;
    canvas.height = 1200;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#f6f3ee";
    ctx.fillRect(0, 0, 900, 1200);
    ctx.fillStyle = "#1c1917";
    ctx.font = "500 64px Georgia";
    ctx.fillText("MIJI", 64, 110);
    ctx.font = "28px Georgia";
    ctx.fillText(reading.shapeName, 64, 180);
    ctx.font = "20px sans-serif";
    wrapText(ctx, reading.summary, 64, 230, 760, 30);
    ctx.font = "18px sans-serif";
    reading.characters.forEach((c, i) => ctx.fillText(c, 64 + (i % 3) * 250, 430 + Math.floor(i / 3) * 36));
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "miji-hasil-wajah.png";
    a.click();
    setNotice("Hasil tersimpan sebagai gambar.");
  };

  const changeGender = (next) => {
    setGender(next);
    setFamilyId("");
    setStyleId("");
    setStyleResults(null);
    setColorResults(null);
    setSheetError("");
  };

  const selectFamily = (id) => {
    setFamilyId(id);
    setStyleId("");
    setStyleResults(null);
    setColorResults(null);
    setSheetError("");
  };

  const selectResult = (id) => {
    setStyleId(id);
    setColorResults(null);
    setSheetError("");
  };

  const applyStyle = async () => {
    if (!photoUrl || !points || !activeStyle) return;
    setSheetBusy("styles");
    setSheetError("");
    setColorResults(null);
    try {
      const url = await requestHairFront({ photoUrl, points, style: activeStyle });
      setStyleResults((prev) => ({ ...(prev || {}), [activeStyle.id]: url }));
    } catch (e) {
      setSheetError(e.message || "Model belum berhasil dipasang.");
    } finally {
      setSheetBusy("");
    }
  };

  const applyStyles = async () => {
    const styles = activeFamily?.variants || [];
    if (!photoUrl || !points || styles.length < 1) return;
    setSheetBusy("styles");
    setSheetError("");
    setColorResults(null);
    try {
      const results = await requestHairSheet({ photoUrl, points, styles });
      setStyleResults(results);
    } catch (e) {
      setSheetError(e.message || "Varian belum berhasil dibuat.");
    } finally {
      setSheetBusy("");
    }
  };

  const applyColor = async (hex) => {
    const selectedResult = activeStyle ? styleResults?.[activeStyle.id] : "";
    if (!activeStyle || !selectedResult || !hex) return;
    setSheetBusy("colors");
    setSheetError("");
    try {
      let colorPhoto = { photoUrl: selectedResult, points };
      try {
        colorPhoto = await readPhoto(selectedResult);
      } catch (readError) {
        // Keep the locked front landmarks if the edited face is slightly reframed.
      }
      const url = await requestHairColor({
        photoUrl: colorPhoto.photoUrl,
        points: colorPhoto.points,
        style: activeStyle,
        color: hex,
      });
      setColorResults((prev) => ({ ...(prev || {}), [hex]: url }));
    } catch (e) {
      setSheetError(e.message || "Warna belum terpasang.");
    } finally {
      setSheetBusy("");
    }
  };

  const shareResult = async () => {
    const text = reading
      ? `Bentuk wajah ${reading.shapeName}. Model: ${families.slice(0, 3).map((s) => s.name).join(", ")}. Warna: ${colors.map((c) => c.name).join(", ")}.`
      : "Konsultasi gaya rambut MIJI.";
    try {
      if (navigator.share) await navigator.share({ title: "MIJI Haircare", text });
      else {
        await navigator.clipboard.writeText(text);
        setNotice("Ringkasan disalin.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") setNotice("Bagikan tidak tersedia di browser ini.");
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f6f3ee] text-[#1c1917]" style={{ fontFamily: '"Manrope", Inter, sans-serif' }} data-testid="hair-consult">
      {!unlocked && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#f6f3ee] px-4">
          <form onSubmit={gateForgot ? submitGateForgot : submitGate} className="w-full max-w-md rounded-3xl border border-[#ece6de] bg-white p-8" data-testid="visitor-gate">
            <div className="text-[11px] tracking-[0.22em] text-[#8a8178]">{site?.brand?.sub || "MIJI HAIRCARE"}</div>
            <h1 className="mt-2 text-4xl" style={{ fontFamily: serif }}>{site?.visitorGate?.title || "Akses Konsultasi"}</h1>
            <p className="mt-3 text-sm leading-relaxed text-[#6d655e]">
              {gateForgot
                ? "Password baru akan dikirim ke email terdaftar salon."
                : (site?.visitorGate?.text || "Masukkan kode yang diberikan salon untuk memakai analisa dan try-on.")}
            </p>
            {!gateForgot && (
              <div className="mt-6">
                <PasswordField
                  label="Kode akses"
                  value={gatePassword}
                  onChange={(e) => setGatePassword(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-[#e4ddd4] bg-white px-3 py-2 pr-28 text-sm"
                  autoFocus
                />
              </div>
            )}
            {gateError && <p className="mt-3 text-sm text-rose-700">{gateError}</p>}
            {gateNotice && <p className="mt-3 text-sm text-emerald-800">{gateNotice}</p>}
            <button type="submit" disabled={gateBusy} className="mt-6 w-full rounded-full bg-[#1c1917] py-3 text-sm text-white disabled:opacity-50">
              {gateBusy ? "Memeriksa…" : gateForgot ? "Kirim password" : "Masuk"}
            </button>
            <button
              type="button"
              className="mt-3 w-full text-sm text-[#6d655e] underline"
              onClick={() => { setGateForgot((v) => !v); setGateError(""); setGateNotice(""); }}
            >
              {gateForgot ? "Kembali masuk" : "Lupa kode akses?"}
            </button>
          </form>
        </div>
      )}
      <Header view={view} onNav={go} onRestart={restart} brand={site?.brand} />
      {view === "home" && <Home onStart={() => go("upload")} brand={site?.brand} />}
      {view === "upload" && (
        <UploadStep
          step={step}
          frontSource={frontSource}
          busy={busy}
          error={error}
          cameraSlot={cameraSlot}
          videoRef={videoRef}
          gender={gender}
          onGender={changeGender}
          onFile={onFile}
          onOpenCamera={(slot) => { setError(""); setCameraSlot(slot); }}
          onCapture={onCapture}
          onAnalyze={analyzePhotos}
        />
      )}
      {view === "result" && (
        <ResultStep
          reading={reading}
          photoUrl={photoUrl}
          points={points}
          width={photoSize.width}
          height={photoSize.height}
          gender={gender}
          onGender={changeGender}
          colors={colors}
          families={families}
          activeFamilyId={activeFamily?.id || ""}
          activeStyle={activeStyle}
          onFamily={selectFamily}
          onSelectStyle={selectResult}
          onApply={applyStyle}
          onApplyAll={applyStyles}
          onColor={applyColor}
          allowApplyAll={site?.tryOn?.allowApplyAll !== false}
          sheetBusy={sheetBusy}
          sheetError={sheetError}
          styleResults={styleResults}
          selectedStyleId={styleId}
          colorResults={colorResults}
        />
      )}
      {view === "about" && <About brand={site?.brand} />}
      {booking && <Booking onClose={() => setBooking(false)} brand={site?.brand} />}
    </div>
  );
}

function wrapText(ctx, text, x, y, max, lh) {
  const words = text.split(" ");
  let line = "";
  let yy = y;
  words.forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > max) {
      ctx.fillText(line, x, yy);
      line = word;
      yy += lh;
    } else line = test;
  });
  if (line) ctx.fillText(line, x, yy);
}
